
import { verifyAliyunCaptcha } from "@/utils/aliyunCaptcha";
import { trackRegTrigger } from "@/plugins/hwtag/events";

const EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PWD_REG = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{6,16}$/;

export default {
  name: "AuthLoginModal",
  props: {
    showPasswordFree: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: false,
      activeTab: "login",
      loginLoading: false,
      appleLoading: false,
      sendCodeLoading: false,
      registerLoading: false,
      isGoogleInit: false,
      isAppleInit: false,
      googleClickTracked: false,
      googleClickResetTimer: null,
      // 单次弹窗会话内, 切到 register tab 是否已上报 reg_trigger 埋点
      regTriggerSent: false,
      authTriggerSource: null,
      /* 倒计时 */
      codeCounting: false,
      codeCountdown: 60,
      codeTimer: null,

      loginForm: {
        email: "",
        password: "",
      },

      registerForm: {
        email: "",
        password: "",
        code: "",
        subscribe: false,
      },
    };
  },

  computed: {
    shouldRedirectToUserCenter() {
      const req = this.$store.state.authModalRequest;
      return req && req.redirectToUserCenter !== false;
    },
    canSendCode() {
      return EMAIL_REG.test(this.registerForm.email) && PWD_REG.test(this.registerForm.password);
    },
    loginRules() {
      return {
        email: [
          {
            required: true,
            message: this.$t("page.INPUT_EMAIL"),
            trigger: "blur",
          },
          {
            pattern: EMAIL_REG,
            message: this.$t("page.VALID_EMAIL_REQUIRED"),
            trigger: "blur",
          },
        ],
        password: [
          {
            required: true,
            message: this.$t("page.ENTER_PASSWORD"),
            trigger: "blur",
          },
        ],
      };
    },

    registerRules() {
      return {
        email: [
          {
            required: true,
            message: this.$t("page.INPUT_EMAIL"),
            trigger: "blur",
          },
          {
            pattern: EMAIL_REG,
            message: this.$t("page.VALID_EMAIL_REQUIRED"),
            trigger: "blur",
          },
        ],
        password: [
          {
            required: true,
            message: this.$t("page.ENTER_PASSWORD"),
            trigger: "blur",
          },
          {
            pattern: PWD_REG,
            message: this.$t("page.PASSWORD_RULE_TIP"),
            trigger: "blur",
          },
        ],
        code: [
          {
            required: true,
            message: this.$t("page.ENTER_VERIFY_CODE"),
            trigger: "blur",
          },
          {
            pattern: /^\d{6}$/,
            message: this.$t("page.INPUT_6DIGIT_CODE"),
            trigger: "blur",
          },
        ],
      };
    },
  },

  mounted() {
    this.addGoogleClickListener();
  },

  beforeDestroy() {
    this.clearCodeTimer();
    this.removeGoogleClickListener();
  },

  methods: {
    /* ---- 对外接口 ---- */
    openDialog(tab = "login", email = "", source = null) {
      this.activeTab = tab;
      const storeSource = this.$store.state.authModalRequest && this.$store.state.authModalRequest.source;
      this.authTriggerSource = source || storeSource || null;
      if (email) {
        this.loginForm.email = email;
        this.registerForm.email = email;
      }
      this.dialogVisible = true;
      // 每次打开弹窗重置, 保证一次新会话第一次切到 register tab 仍会上报
      this.regTriggerSent = false;
      // 如果直接以 register tab 打开, 等同于"切换到注册"动作, 立即触发一次 reg_trigger
      if (tab === "register") {
        this.fireRegTrigger();
      }
      if (tab === "login") {
        this.$nextTick(() => {
          this.initGoogleBtn();
          this.initAppleBtn();
        });
      }
    },

    /* ---- 切换到注册 tab 时, 根据弹窗触发来源上报 reg_trigger ---- */
    fireRegTrigger() {
      if (this.regTriggerSent) return;
      const source = this.authTriggerSource ||
        (this.$store.state.authModalRequest && this.$store.state.authModalRequest.source);
      if (!source || !source.elementTypeName) return;
      try {
        trackRegTrigger(
          '点击',
          source.elementTypeName,
          source.clickName || '',
          {},
          source.loginChannel || ''
        );
        this.regTriggerSent = true;
      } catch (e) {
        // ignore
      }
    },

    closeDialog() {
      this.dialogVisible = false;
      this.resetForms();
    },

    handleGoogleClick() {
      trackRegTrigger('点击', '中间的活动浮窗', 'Google');
    },

    detectGoogleIframeClick() {
      // Google 官方按钮是 iframe, 点击事件不会冒泡到父级 div.
      if (this.googleClickTracked) return;
      this.googleClickTracked = true;
      setTimeout(() => {
        const active = document.activeElement;
        const hit =
          active &&
          active.tagName === 'IFRAME' &&
          active.closest &&
          active.closest('#g_id_onload');

        if (hit) {
          this.handleGoogleClick();
          if (this.googleClickResetTimer) clearTimeout(this.googleClickResetTimer);
          this.googleClickResetTimer = setTimeout(() => {
            this.googleClickTracked = false;
            this.googleClickResetTimer = null;
          }, 1500);
        } else {
          this.googleClickTracked = false;
        }
      }, 0);
    },

    addGoogleClickListener() {
      window.addEventListener("blur", this.detectGoogleIframeClick);
    },

    removeGoogleClickListener() {
      window.removeEventListener("blur", this.detectGoogleIframeClick);
      if (this.googleClickResetTimer) {
        clearTimeout(this.googleClickResetTimer);
        this.googleClickResetTimer = null;
      }
    },

    /* ---- Tab 切换携带邮箱 ---- */
    onTabSwitch(tab) {
      if (tab.name === "register") {
        this.registerForm.email = this.loginForm.email;
        this.fireRegTrigger();
      } else {
        this.loginForm.email = this.registerForm.email;
        this.$nextTick(() => {
          this.initGoogleBtn();
          this.initAppleBtn();
        });
      }
    },

    /* ---- Google 登录 ---- */
    async initGoogleBtn() {
      if (this.isGoogleInit) return;
      if (!this.$googleSignin) return;
      try {
        await this.$googleSignin.init(this.handleGoogleLogin);
        this.$googleSignin.renderButton("g_id_onload", {
          theme: "outline",
          size: "large",
          type: "icon",
        });
        this.isGoogleInit = true;
      } catch (e) {
        console.error("[AuthLoginModal] Google init failed:", e);
        // init 失败时不设 isGoogleInit, 下次打开弹窗可重试
      }
    },
    async initAppleBtn() {
      if (this.isAppleInit) return;
      if (!this.$appleSignin) return;
      try {
        await this.$appleSignin.init({
          redirectURI: window.location.origin,
          // redirectURI: 'https://xfanstest.xp-pen.com/user/gallery',
        });


        this.isAppleInit = true;

      } catch (error) {
        console.error("Apple SignIn init error:", error);
      }
    },
    async handleAppleLogin() {
      trackRegTrigger('点击', '中间的活动浮窗', 'Apple');
      if (this.appleLoading) return;
      if (!this.$appleSignin) return;

      this.appleLoading = true;
      try {
        if (!this.isAppleInit) {
          await this.initAppleBtn();
        }

        const appleAuth = await this.$appleSignin.login();
        if (!appleAuth) {
          this.$message.error(this.$t('page.APPLE_LOGIN_FAILED'));
          return;
        }

        // Apple 一般返回 id_token 与 code，这里优先传 id_token，缺失时回退 code
        const res = await this.$global.thirdPartyLogin(
          {
            third_party_token: appleAuth.code,
            third_party_type: "8", // 7: Apple（按后端约定） ios主体变更后为8
          },
          {
            jumpUrl: window.location.origin + "/user/gallery",
            lang: this.$i18n.locale || "en",
          }
        );

        if (res.code == 1) {
          this.$store.commit("SETTOKEN", res.data);
          const result = await this.$global.xfansLogin(res.data); // 子站社区登录校验
          this.$cookies.set("SSOTOKEN", res.data.token, {
            maxAge: 60 * 60 * 24 * 7,
          });
          this.$cookies.set("lastVerifiedTime", new Date().getTime()); // 校验上一次登录时间

          if (result.code == 1) {
            this.$store.commit("USER_INFO", result.data);
            this.$store.commit("ISLOGIN", true);
            this.$store.state.report = false;
            localStorage.setItem("storeState", JSON.stringify(this.$store.state));
            this.closeDialog();
            if (this.shouldRedirectToUserCenter) {
              this.$router.push("/user/gallery");
            }
          } else {
            this.$store.commit("SIGNOUT");
            this.$message.warning(this.$t('page.ACCOUNT_RESTRICTED'));
          }
        } else {
          this.$store.commit("SIGNOUT");
          this.$message.error(res?.msg || this.$t('page.APPLE_LOGIN_FAILED'));
        }
      } catch (error) {
        console.error("Apple login error:", error);
        this.$message.error(error?.message || this.$t('page.APPLE_LOGIN_FAILED'));
      } finally {
        this.appleLoading = false;
      }
    },
    async handleGoogleLogin(response) {
      console.log("Google 登录成功，Token:", response.credential);
      this.$global.thirdPartyLogin({
        third_party_token: response.credential,
        third_party_type: '3', // 3: Google
        google_web_push:"0"

      }, {
        jumpUrl: window.location.origin + '/user/gallery',
        lang: this.$i18n.locale || "en",
      }).then(async res => {
        if (res.code == 1) {
          this.$store.commit("SETTOKEN", res.data);
          let result = await this.$global.xfansLogin(res.data); //子站社区登录校验
          this.$cookies.set("SSOTOKEN", res.data.token, { maxAge: 60 * 60 * 24 * 7 });
          this.$cookies.set("lastVerifiedTime", new Date().getTime()); //校验上一次登录时间
          result.code == 1
            ? (this.$store.commit("USER_INFO", result.data),
              this.$store.commit("ISLOGIN", true))
            : (this.$store.commit("SIGNOUT"),
              (this.disableUserDialogVisible = true),
              this.signOut());
          this.$store.state.report = false;
          localStorage.setItem("storeState", JSON.stringify(this.$store.state));
          if (this.shouldRedirectToUserCenter) {
            this.$router.push("/user/gallery");
          }
        } else {
          this.$store.commit("SIGNOUT");
        }
      });
      // TODO: 发送 token 到后端
      this.closeDialog();
    },



    /* ---- 登录 ---- */
    async handleLogin() {
      if (this.loginLoading) return;
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return;
        this.loginLoading = true;
        try {
          // 获取场景ID
          const sceneRes = await this.$global.getSceneId();
          const sceneId =
            sceneRes?.data?.encrypted_scene_id || sceneRes?.data || "";
          if (!sceneId) {
            this.$message.error(this.$t('page.GET_VERIFICATION_SCENE_FAILED'));
            return;
          }
          // 获取无痕验证码
          const captchaResult = await verifyAliyunCaptcha({
            EncryptedSceneId: sceneId,
            language: "en",
          });
          // 调用社区登录接口
          const res = await this.$global.passportLogin({
            email: this.loginForm.email,
            password: this.loginForm.password,
            captcha: captchaResult,
          },{
            jumpUrl: window.location.origin + '/user/gallery',
            lang: "en",
          });
          if (res.code == 1) {
            this.$message.success(this.$t('page.LOGIN_SUCCESS'));
            this.closeDialog();
            this.$store.commit("SETTOKEN", res.data);
            let result = await this.$global.xfansLogin(res.data); //子站社区登录校验
            this.$cookies.set("SSOTOKEN", res.data.token, {
              maxAge: 60 * 60 * 24 * 7,
            });
            this.$cookies.set("lastVerifiedTime", new Date().getTime()); //校验上一次登录时间
            result.code == 1
              ? (this.$store.commit("USER_INFO", result.data),
                this.$store.commit("ISLOGIN", true))
              : (this.$store.commit("SIGNOUT"),
                (this.disableUserDialogVisible = true),
                this.signOut());
            this.$store.state.report = false;
            localStorage.setItem(
              "storeState",
              JSON.stringify(this.$store.state)
            );
            if (this.shouldRedirectToUserCenter) {
              this.$router.push("/user/gallery");
            }
          } else {
            this.$message.error(res?.msg || this.$t('page.LOGIN_FAILED'));
            this.$store.commit("SIGNOUT");
          }
        } catch (error) {
          console.error("Login error:", error);
          if (error?.message === "Captcha closed by user") return;

          this.$message.error(error.message || this.$t('page.SYSTEM_ERROR'));
        } finally {
          this.loginLoading = false;
        }

      });
    },

    /* ---- 注册 ---- */
    async handleRegister() {
      this.$refs.registerForm.validate(async (valid) => {
        if (!valid) return;

        this.registerLoading = true;
        try {
          // 1. 先进行验证码校验
          const sceneRes = await this.$global.getSceneId();
          const sceneId =
            sceneRes?.data?.encrypted_scene_id || sceneRes?.data || "";
          if (!sceneId) {
            this.$message.error(this.$t('page.GET_VERIFICATION_SCENE_FAILED'));
            return;
          }
          const captchaResult = await verifyAliyunCaptcha({
            EncryptedSceneId: sceneId,
            language: "en",
          });

          if (!captchaResult) {
            this.$message.error(this.$t('page.VERIFICATION_FAILED'));
            return;
          }

          // 2. 验证码通过后，发送注册请求
          const params = {
            email: this.registerForm.email,
            password: this.registerForm.password,
            verif_code: this.registerForm.code,
            is_agree: this.registerForm.subscribe ? 1 : 0,
            captcha: captchaResult, // 放入参数
          };

          const res = await this.$global.passportRegister(params, {
            jumpUrl: window.location.origin + '/user/gallery',
            lang: this.$i18n.locale || "en",
          });


          if (res.code == 1) {
            this.$message.success((res && res.msg) || this.$t('page.REGISTER_SUCCESS'));
            this.$store.commit("SETTOKEN", res.data);

            let result = await this.$global.xfansLogin(res.data); //子站社区注册校验
            this.$cookies.set("SSOTOKEN", res.data.token, {
              maxAge: 60 * 60 * 24 * 7,
            });
            this.$cookies.set("lastVerifiedTime", new Date().getTime()); //校验上一次登录时间
            result.code == 1
              ? (this.$store.commit("USER_INFO", result.data),
                this.$store.commit("ISLOGIN", true))
              : (this.$store.commit("SIGNOUT"),
                (this.disableUserDialogVisible = true),
                this.signOut());
            this.$store.state.report = false;
            localStorage.setItem(
              "storeState",
              JSON.stringify(this.$store.state)
            );
            this.closeDialog();
            if (this.shouldRedirectToUserCenter) {
              this.$router.push("/user/gallery");
            }
          } else {
            this.$message.error(res?.msg || this.$t('page.REGISTER_FAILED'));
            this.$store.commit("SIGNOUT");
          }




          // 建议：注册成功后跳转到登录页，除非后端自动登录并返回了 token
          // this.$router.push("/login?registered=1");
        } catch (error) {
          console.error("Register error:", error);
          if (error?.message === "Captcha closed by user") return;

          this.$message.error(error.message || this.$t('page.SYSTEM_ERROR'));
        } finally {
          this.registerLoading = false;
        }
      });
    },

    /* ---- 发送验证码 ---- */
    async sendVerificationCode() {
      const email = this.registerForm.email;
      const password = this.registerForm.password;

      // 1. 前端基础校验（邮箱 + 密码）
      if (!email || !EMAIL_REG.test(email)) {
        this.$refs.registerForm.validateField("email");
        return;
      }
      if (!password || !PWD_REG.test(password)) {
        this.$refs.registerForm.validateField("password");
        return;
      }

      if (this.codeCounting) return;

      try {
        /* ---------- 2. 触发阿里云无感验证 ---------- */
        const sceneRes = await this.$global.getSceneId();
        const sceneId =
          sceneRes?.data?.encrypted_scene_id || sceneRes?.data || "";
        if (!sceneId) {
          this.$message.error(this.$t('page.GET_VERIFICATION_SCENE_FAILED'));
          return;
        }

        const captchaResult = await verifyAliyunCaptcha({
          EncryptedSceneId: sceneId,
          language: "en",
        });

        if (!captchaResult) {
          this.$message.error(this.$t('page.VERIFICATION_FAILED'));
          return;
        }

        console.log(captchaResult, ">>>>>1");

        /* ---------- 3. 调用发送验证码接口 ---------- */
        this.sendCodeLoading = true;
        const res = await this.$global.sendCode({
          email,
          captcha: captchaResult,
        });

        const success = [200, "200", 1, "1"].includes(res?.code);

        if (success) {
          this.$message.success(res?.msg || this.$t('page.CODE_SENT'));
          this.startCodeCountdown();
          this.sendCodeLoading = false;
        } else {
          this.$message.error(res?.msg || this.$t('page.SEND_FAILED'));
          this.sendCodeLoading = false;
        }
      } catch (error) {
        console.error("Send code error:", error);

        if (error?.message === "Captcha closed by user") return;
        if (error?.message === "Captcha request replaced") return;

        const msg =
          error?.message ||
          (error?.raw?.verifyCode
            ? `${this.$t('page.VERIFICATION_FAILED')}: ${error.raw.verifyCode}`
            : this.$t('page.SEND_FAILED'));

        this.$message.error(msg);
      }
    },

    startCodeCountdown() {
      this.codeCounting = true;
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown -= 1;
        if (this.codeCountdown <= 0) {
          this.clearCodeTimer();
        }
      }, 1000);
    },

    clearCodeTimer() {
      if (this.codeTimer) {
        clearInterval(this.codeTimer);
        this.codeTimer = null;
      }
      this.codeCounting = false;
      this.codeCountdown = 60;
    },

    /* ---- 跳转其他弹窗 ---- */
    openForgotPassword() {
      const email = this.loginForm.email;
      this.closeDialog();
      this.$emit("open-forgot-password", email);
    },

    openPasswordFree() {
      const email = this.loginForm.email;
      this.closeDialog();
      this.$emit("open-password-free", email);
    },

    /* ---- 重置 ---- */
    resetForms() {
      this.$nextTick(() => {
        this.$refs.loginForm && this.$refs.loginForm.resetFields();
        this.$refs.registerForm && this.$refs.registerForm.resetFields();
        this.clearCodeTimer();
        this.isGoogleInit = false;
        this.isAppleInit = false;
        // 同步重置插件内部状态, 使下次 init() 能重新调用 renderButton
        if (this.$googleSignin && typeof this.$googleSignin.resetInitialized === 'function') {
          this.$googleSignin.resetInitialized();
        }
      });
    },
  },
};
